import React, { MouseEvent } from 'react';
import { Markdown } from '../../../../../../ui';
import styled from '@emotion/styled';
import {
	StyledParkingProductCard,
	StyledParkingProductCardContent,
	StyledParkingProductCardFooter,
	StyledParkingProductCardHeader,
	StyledParkingProductCardTitle,
} from './StyledParkingProductCard';
import { useTranslation } from '../../../../../shared/i18n/translate/useTranslation';
import { ParkingProductPrice } from './ParkingProductPrice';
import { ShowDetailsButton } from './ShowDetailsButton';
import { ParkingProductTransferDetails } from './ParkingProductTransferDetails';
import { IParkingProduct } from '../../../../domain/parking-product/models/IParkingProduct';
import { trackClickedParkingProduct } from '../../shared/analytics/custom/trackClickedParkingProduct';
import ParkingProductSpotsRemaining from './ParkingProductSpotsRemaining';
import { SubmitButton } from '../../../../../shared/components';
import Responsive from '../../../../../../ui/utils/Responsive';
import { theme } from '../../../../../shared/styles';

interface IParkingProductCardProps {
	parkingProduct: IParkingProduct;
	selected?: boolean;
	onClick: () => void;
}

export const ParkingProductCard = ({
	parkingProduct,
	selected,
	onClick,
}: IParkingProductCardProps) => {
	const { t } = useTranslation();

	const { isAvailable } = parkingProduct.availability;

	const handleClick = (e: MouseEvent<HTMLDivElement>) => {
		e.preventDefault();

		if (!isAvailable) {
			return;
		}

		trackClickedParkingProduct(parkingProduct);
		onClick();
	};

	return (
		<StyledParkingProductCard
			onClick={handleClick}
			disabled={!isAvailable}
			selected={selected}
			aria-current={selected}
			role="listitem"
		>
			<div>
				<StyledParkingProductCardHeader>
					{parkingProduct.logo && <img src={parkingProduct.logo} alt="" />}
					<ShowDetailsButton
						parkingProduct={parkingProduct}
						variation={parkingProduct.logo ? 'inverted' : 'highlight'}
					/>
				</StyledParkingProductCardHeader>
				<StyledParkingProductCardContent>
					<StyledParkingProductCardTitle id="parking-card-header">
						{parkingProduct.name}
					</StyledParkingProductCardTitle>
					{parkingProduct.transferDetails && (
						<ParkingProductTransferDetails
							{...parkingProduct.transferDetails}
						/>
					)}
					{parkingProduct.spotsRemainingForPrice && (
						<ParkingProductSpotsRemaining
							spotsAmount={parkingProduct.spotsRemainingForPrice}
						/>
					)}
					<ul>
						{parkingProduct.features.map((feature) => (
							<li key={feature}>
								<Markdown source={feature} />
							</li>
						))}
					</ul>
				</StyledParkingProductCardContent>
			</div>

			<StyledParkingProductCardFooter>
				<StyledParkingProductPrice
					loyaltyPoints={parkingProduct.loyaltyPoints}
					price={parkingProduct.price.value}
					originalPrice={parkingProduct.price.originalValue}
				/>

				{isAvailable ? (
					<StyledLinkbutton
						aria-describedby="parking-card-header"
						name={parkingProduct.name}
					>
						{t(
							selected
								? 'web_parking_availability_results_select_product_button_selected'
								: 'web_parking_availability_results_select_product_button',
							{ name: parkingProduct.name },
						)}
					</StyledLinkbutton>
				) : (
					<StyledLinkbutton disabled>
						{t('parking_options_sold_out_online')}
					</StyledLinkbutton>
				)}
			</StyledParkingProductCardFooter>
		</StyledParkingProductCard>
	);
};

const StyledParkingProductPrice = styled(ParkingProductPrice)`
	display: flex;
	margin-bottom: 20px;
	flex-direction: row;
`;

const StyledLinkbutton = styled(SubmitButton)`
	font-family: inherit;
	justify-content: center;
	padding: 14px 16px;
	border-radius: 4px;
	box-shadow: 0px -3px 0px 0px #005b99 inset;
	height: 50px;

	&:focus-visible {
		outline: ${theme.button?.primary?.focus?.outline};
	}

	@media (${Responsive.getMediaQueryForBreakpoint('mobile')}) {
		width: 100%;
	}
`;
